/**
 * API methods that rely on both Duolingo TTS and TTS Generation Backend
 */

import Axios from "axios";

import type { DuolingotTtsVoiceData } from "./duolingoTts";
import { getDuolingoTtsRecordedVoices } from "./duolingoTts";
import type {
  TtsGenerationAge,
  TtsGenerationGender,
  TtsGenerationProvider,
  VoicesV2Item,
} from "./ttsGenerationBackend";
import { getTtsGenerationVoiceList } from "./ttsGenerationBackend";

export interface VoiceListVoice extends Omit<VoicesV2Item, "provider"> {
  addedAt?: Date;
  collections: string[];
  lastUpdatedAt?: Date;
  getRawData: () => VoicesV2Item | DuolingotTtsVoiceData;
  provider: TtsGenerationProvider | "recorded";
}

export const getCombinedVoiceList = async (): Promise<VoiceListVoice[]> => {
  const [ttsGenList, recordedVoicesList] = await Promise.all([
    getTtsGenerationVoiceList(),
    getDuolingoTtsRecordedVoices(),
  ]);

  const ttsVoices = ttsGenList.map(ttsGenVoice =>
    toVoiceListVoice(ttsGenVoice),
  );
  const recordedVoices =
    recordedVoicesList?.map(recordedVoice => ({
      added_at: null,
      added_by: "Duolingo",
      get addedAt() {
        return undefined;
      },
      age: recordedVoice.age as TtsGenerationAge,
      collections: [],
      friendly_name: null,
      gender: recordedVoice.gender as TtsGenerationGender,
      getRawData: () => recordedVoice,
      id: recordedVoice.name,
      is_default_version: true,
      is_expressive: false,
      is_multilingual: false,
      isUsedInLearningApp: true,
      language_statuses: [],
      last_updated_at: null,
      last_updated_by: "Duolingo",
      get lastUpdatedAt() {
        return undefined;
      },
      name: recordedVoice.name,
      notes: "",
      primary_language: recordedVoice.language,
      provider: "recorded" as const,
      provider_parameters: {
        model_name: "recorded",
      },
      speed: null,
      status: "active" as const,
      tags: [],
      use_cases: [],
      version: 1,
      world_character: null,
    })) ?? [];
  return [...ttsVoices, ...recordedVoices];
};

export const getAudioMp3 = async (url: string) =>
  (
    await Axios.get<Blob>(url, {
      responseType: "blob",
    })
  ).data;

export const toVoiceListVoice = (voice: VoicesV2Item): VoiceListVoice => ({
  ...voice,
  get addedAt() {
    return voice.added_at === null ? undefined : new Date(voice.added_at);
  },
  get collections() {
    return voice.tags
      .filter(tag => tag.startsWith("Collection: "))
      .map(tag => tag.slice("Collection: ".length));
  },
  getRawData: () => voice,
  get lastUpdatedAt() {
    return voice.last_updated_at === null
      ? undefined
      : new Date(voice.last_updated_at);
  },
});
